import { Flex, HStack, Text } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { TEvaluationComponentProps } from "@src/components/modules/projects/detail/Evaluation/types";
import {
  getProfit,
  getProfitAvgPercent,
} from "@src/components/modules/projects/detail/Evaluation/utils";
import { CardHeader } from "@src/components/modules/projects/detail/Stats/CardHeader";
import { Card } from "@src/components/ui-kit";
import { currency, percent } from "@src/utils/formatters";
import type { FC } from "react";

type ProfitBarProps = {
  value: number;
  variant?: "normal" | "big";
};

export const ProfitBar: FC<ProfitBarProps> = ({
  value,
  variant = "normal",
}) => {
  const height = variant === "big" ? 6 : 3;
  const dividedValue = value / 100;
  const formattedValue = percent.format(value);
  const barWidth = `${Math.abs(dividedValue / 2)}%`;
  const textOffset = dividedValue > 15 || dividedValue < -15 ? "15%" : barWidth;

  const leftBarPos = value > 0 ? "50%" : undefined;
  const rightBarPos = value < 0 ? "50%" : undefined;
  const leftTextPos = value > 0 ? `calc(50% + ${textOffset})` : undefined;
  const rightTextPos = value < 0 ? `calc(50% + ${textOffset})` : undefined;

  return (
    <Flex
      pos="relative"
      overflow="hidden"
      w="150px"
      h={height}
      bg="grey.100"
      role="group"
      rounded="3xl"
    >
      <Flex
        pos="absolute"
        right={rightBarPos}
        left={leftBarPos}
        w={barWidth}
        h={height}
        bg={value > 0 ? "teal.500" : "red.500"}
      />
      {variant === "big" && (
        <Flex
          pos="absolute"
          right={rightTextPos}
          left={leftTextPos}
          align="center"
          justify="center"
          h={height}
          pr={value < 0 ? "2" : 0}
          pl={value > 0 ? "2" : 0}
          color="transparent"
          _groupHover={{
            color: "black",
          }}
          transition="150ms ease-in"
        >
          <Text h="0" lineHeight="0">
            {formattedValue}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

type ProfitRowProps = {
  title: string;
  value: number;
  percentValue: number;
  /**
   * Multiplies the percent value that is used as width in bar (2.5 will mean 40% in percentValue
   * will fill the bar with green)
   */
  barMultiplier?: number;
};

export const ProfitRow: FC<ProfitRowProps> = ({
  title,
  value,
  percentValue,
  barMultiplier = 1,
}) => {
  const formattedValue = currency.formatByWorkspace(value);
  const formattedPercent = percent.format(percentValue);

  return (
    <HStack justifyContent="space-between" w="full">
      <Text>{title}</Text>
      <HStack spacing="4">
        <Text color={percentValue < 0 || value < 0 ? "red.500" : undefined}>
          {`${formattedValue} (${formattedPercent})`}
        </Text>
        <ProfitBar value={percentValue * barMultiplier} />
      </HStack>
    </HStack>
  );
};

export const ProfitCard: FC<TEvaluationComponentProps> = ({ project }) => {
  const { net_profit, net_margin_percent, ourWork } = project.stats;

  const productionBudgetItems =
    project.expensesStructured.find(({ name }) => name === "Production")
      ?.budgetItems ?? [];
  const mediaBudgetItems =
    project.expensesStructured.find(({ name }) => name === "Media")
      ?.budgetItems ?? [];

  return (
    <Card p="4">
      <HStack alignItems="flex-start" justifyContent="space-between">
        <CardHeader title={t`Project profit`} />
        <HStack spacing="4">
          <Text fontSize="2xl" fontWeight="bold">
            {currency.formatByWorkspace(net_profit)}
          </Text>
          <ProfitBar value={net_margin_percent} variant="big" />
        </HStack>
      </HStack>
      <ProfitRow
        title={t`Profit (Work)`}
        value={ourWork.net_profit}
        percentValue={ourWork.net_margin_percent}
      />
      <ProfitRow
        title={t`Profit (Production)`}
        value={getProfit(productionBudgetItems)}
        percentValue={getProfitAvgPercent(productionBudgetItems)}
      />
      <ProfitRow
        title={t`Profit (Media)`}
        value={getProfit(mediaBudgetItems)}
        percentValue={getProfitAvgPercent(mediaBudgetItems)}
      />
    </Card>
  );
};
